import { useEffect, useState } from "react";
import ScrollReveal from "@/components/ScrollReveal";
import { Eye, Droplets, ShieldCheck, Activity, Sparkles, ArrowUpRight } from "lucide-react";
import { fetchClinicalApps } from "@/lib/api";

const ICON_MAP = { Eye, Droplets, ShieldCheck, Activity, Sparkles };

export default function ClinicalApplications() {
  const [apps, setApps] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchClinicalApps()
      .then((r) => {
        setApps(r);
        setActive(r[0]?.id);
      })
      .catch(() => {});
  }, []);

  return (
    <section data-testid="clinical-applications" className="bg-white py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.22em] uppercase text-teal font-semibold">
                Clinical Applications
              </div>
              <h2 className="mt-5 font-display text-4xl md:text-5xl text-slate1 leading-tight">
                Built for the cases that need more than drops.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-slate1/65 leading-relaxed">
                XcellerEYES has been used by providers across the U.S. for a
                broad spectrum of ocular surface and post-surgical indications.
              </p>
            </div>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app, i) => {
            const Icon = ICON_MAP[app.icon] || Eye;
            const isActive = active === app.id;
            return (
              <ScrollReveal key={app.id} delay={i * 80}>
                <button
                  onClick={() => setActive(app.id)}
                  data-testid={`clinical-card-${app.id}`}
                  className={`group text-left w-full rounded-3xl p-8 transition-all duration-500 ${
                    isActive
                      ? "bg-slate1 text-white shadow-[0_28px_60px_-25px_rgba(0,45,114,0.5)]"
                      : "bg-cloud text-slate1 hover:bg-white hover:shadow-[0_20px_50px_-20px_rgba(0,45,114,0.18)] border border-transparent hover:border-teal/30"
                  } hover:-translate-y-1`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center transition-colors ${
                      isActive ? "bg-teal" : "bg-white border border-slate1/10 group-hover:border-teal"
                    }`}>
                      <Icon className={`h-5 w-5 ${isActive ? "text-white" : "text-navy"}`} />
                    </div>
                    <ArrowUpRight className={`h-5 w-5 ${isActive ? "text-teal2" : "text-slate1/30 group-hover:text-teal"} transition-colors`} />
                  </div>
                  <h3 className="font-display text-xl font-medium mb-3">{app.title}</h3>
                  <p className={`text-sm leading-relaxed ${isActive ? "text-white/70" : "text-slate1/65"}`}>
                    {app.description}
                  </p>
                  <div className={`mt-6 text-[10px] tracking-[0.22em] uppercase font-bold ${isActive ? "text-teal2" : "text-teal"}`}>
                    Indication 0{i + 1}
                  </div>
                </button>
              </ScrollReveal>
            );
          })}

          {/* Special CTA card */}
          <ScrollReveal delay={400}>
            <div className="rounded-3xl p-8 relative overflow-hidden h-full hero-radial text-white flex flex-col justify-between min-h-[280px]">
              <div className="absolute inset-0 grid-bg opacity-30" />
              <div className="relative">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-teal2">Discuss your protocol</div>
                <h3 className="mt-3 font-display text-2xl font-medium">A case we should explore?</h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Talk with a RenVia clinical specialist about how XcellerEYES could fit into your treatment algorithm.
                </p>
              </div>
              <a
                href={process.env.REACT_APP_HUBSPOT_MEETING_LINK_URL || "/contact"}
                target={process.env.REACT_APP_HUBSPOT_MEETING_LINK_URL ? "_blank" : undefined}
                rel={process.env.REACT_APP_HUBSPOT_MEETING_LINK_URL ? "noopener noreferrer" : undefined}
                data-testid="clinical-cta"
                className="relative mt-6 inline-flex items-center gap-2 text-teal2 hover:text-white text-sm font-semibold transition-colors"
              >
                Book a consultation <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
